import { omit } from 'lodash-es';
import { api } from '@/api';
import { RouteActionsType, SurgeonTrip } from '@/api/apis/mission-planner';
import {
  MultiSuggestChain,
  ProcessingStatus,
  SuggestionStatus,
  SuggestionType,
  SuggestionTypeResponse,
} from '@/api/apis/multi-suggest';
import { caseQueries } from '@/queries/case';
import {
  seedCasesInCache,
  seedEmptySuggestions,
  seedLogisticsInCache,
  seedMultiSuggestItems,
} from '@/utils/helpers/queryCache.helpers';
import { DataCacheType } from '@/queries/consts';
import { multiSuggestKeys } from './multi-suggest.keys';
import { multiSuggestQueryClient } from './multi-suggest.utils';

const parseAlgorithmData = async (
  suggestionStatus: SuggestionStatus,
  suggestion: SuggestionTypeResponse,
  caseIds: string[],
): Promise<number> => {
  const isPlansCalculationFinished = suggestion.planStatus === ProcessingStatus.Finished;
  const isPlansCalculationFailed = suggestion.planStatus === ProcessingStatus.Failed;
  const isAllCalculationFinished = suggestionStatus === SuggestionStatus.Finished;
  const isCalculationFinished = isPlansCalculationFinished && isAllCalculationFinished;

  if (isCalculationFinished && suggestion.planSuggestion) {
    const fullPlan = JSON.parse(suggestion.planSuggestion) as MultiSuggestChain[];
    const surgeonTrips = suggestion.surgeonSuggestion
      ? (JSON.parse(suggestion.surgeonSuggestion) as SurgeonTrip[][])
      : [];
    const commercialFlights = suggestion.commercialFlightSuggestion
      ? (JSON.parse(suggestion.commercialFlightSuggestion) as RouteActionsType[][])
      : [];

    await seedMultiSuggestItems(suggestion.id, fullPlan, surgeonTrips, commercialFlights, caseIds);

    return fullPlan.length;
  }

  if (isPlansCalculationFailed) {
    seedEmptySuggestions(suggestion.id, caseIds);
  }

  return 0;
};

export const operateMultiSuggestItemData = async (
  result: SuggestionTypeResponse,
  isCached = false,
): Promise<SuggestionType> => {
  const casesIds = [result.initialCase.caseId, ...result.targetCases.map((c) => c.caseId)];

  if (!isCached) {
    const logisticsList = await Promise.all(
      result.logistics.map(async (item) => api.route.getLogisticsSnapshotByVersion(item.logisticsId, item.version)),
    );

    seedCasesInCache([result.initialCase, ...result.targetCases]);
    seedLogisticsInCache(logisticsList);

    await Promise.all(
      casesIds.map(async (caseId) =>
        multiSuggestQueryClient.prefetchQuery(caseQueries.caseResourcesData(caseId, DataCacheType.Snapshot)),
      ),
    );

    multiSuggestQueryClient.setQueryData(multiSuggestKeys.getReviews(result.id), result.reviews || []);
  }

  const convertedChains = await parseAlgorithmData(result.suggestionStatus, result, casesIds);

  const response = omit(result, ['initialCase', 'targetCases']);

  return {
    ...response,
    caseList: [result.initialCase, ...result.targetCases],
    chainsCount: convertedChains,
  };
};

export const fetchMultiSuggestItem = async (id: string): Promise<SuggestionType> => {
  const cachedData = multiSuggestQueryClient.getQueryData(multiSuggestKeys.getMultiSuggestResultItem(id));
  const result = await api.multiSuggest.getSuggestionById(id);

  return operateMultiSuggestItemData(result, !!cachedData);
};
